import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Shovel, House, Sparkle, Leaf, ShieldCheck, MapPin, Door, HandWaving, Phone, WhatsappLogo } from "@phosphor-icons/react";

// Cotswold stone cottage with garden (Unsplash, free for commercial use)
const HERO_BG = "https://images.unsplash.com/photo-1703291547483-c3cd85e38a83?auto=format&fit=crop&w=2400&q=80";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_honeystone-quote/artifacts/zg8iyn8c_GPT%20LArge.png";
const PHONE_TEL = "tel:+447384361344";
const WHATSAPP_HREF = "https://wa.me/447384361344?text=" + encodeURIComponent("Hi Honeystone, I'd like a quote for my garden/home.");

const SERVICES = [
  { icon: Shovel, title: "Garden Maintenance", body: "Mowing, strimming, weeding & seasonal tidy-ups — one-off or ongoing." },
  { icon: Leaf, title: "Hedges & Pressure Washing", body: "Crisp hedges and clean Cotswold-stone patios. We bring our own tools — no fuss." },
  { icon: House, title: "Handyman Jobs", body: "Small home fixes and odd jobs around the property, done properly." },
];

const STEPS = [
  { n: "01", title: "Send photos & details", body: "Tell us about the job — share a few photos from your phone." },
  { n: "02", title: "Get a fair quote", body: "We'll review and send back a no-obligation quote in plain English." },
  { n: "03", title: "Pick a slot", body: "Accept the quote and choose an available date that suits you." },
];

const VILLAGES = [
  "Moreton-in-Marsh",
  "Bourton-on-the-Water",
  "Stow-on-the-Wold",
  "Chipping Norton",
  "Broadway",
  "Chipping Campden",
  "Blockley",
  "Shipston-on-Stour",
  "Kingham",
  "Snowshill",
  "Bledington",
  "Long Compton",
];

export default function Landing() {
  return (
    <div data-testid="landing-page" className="hs-fade">
      {/* HERO */}
      <section className="relative isolate overflow-hidden">
        <div
          className="absolute inset-0 -z-10 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 -z-10 hero-overlay" />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-28 lg:py-36">
          <div className="max-w-2xl text-white">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-sm border border-white/25 px-3 py-1 text-xs font-medium tracking-wide" data-testid="hero-local-badge">
              <MapPin weight="fill" size={14} /> Moreton-in-Marsh · 20-mile Cotswold radius
            </span>
            <h1 className="mt-5 font-[Outfit] text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight">
              Get a No-Obligation Quote for Your Cotswold Home &amp; Garden
            </h1>
            <p className="mt-4 text-lg sm:text-xl text-white font-medium" data-testid="hero-subheadline">
              Get a no-obligation quote in under 2 minutes.
            </p>
            <p className="mt-3 text-base sm:text-lg text-white/90 max-w-xl">
              Local, friendly garden and handy services across the Cotswolds. Snap a couple of photos, tell us what you need, and we'll send back a fair quote — usually within a day.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/quote" data-testid="hero-get-quote-button">
                <Button className="rounded-full bg-[#4F6F52] hover:bg-[#3C5A3F] text-white px-7 h-12 text-base font-semibold w-full sm:w-auto">
                  Start your quote <ArrowRight weight="bold" className="ml-2" />
                </Button>
              </Link>
              <Link to="/status" data-testid="hero-check-status-button">
                <Button variant="outline" className="rounded-full bg-white/10 backdrop-blur-sm border-white/40 text-white hover:bg-white/20 hover:text-white px-7 h-12 text-base font-semibold w-full sm:w-auto">
                  Check my quote
                </Button>
              </Link>
            </div>
            <p className="mt-4 text-sm text-white/80 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="hero-direct-contact">
              <span>or contact us direct:</span>
              <a href={PHONE_TEL} className="inline-flex items-center gap-1.5 hover:text-white" data-testid="hero-call-link">
                <Phone size={14} weight="fill" /> 07384 361344
              </a>
              <a href={WHATSAPP_HREF} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-white" data-testid="hero-whatsapp-link">
                <WhatsappLogo size={14} weight="fill" /> WhatsApp
              </a>
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/85">
              <span className="flex items-center gap-1.5"><ShieldCheck size={16} weight="fill" /> Insured &amp; local</span>
              <span className="flex items-center gap-1.5"><Sparkle size={16} weight="fill" /> Tidy work</span>
              <span className="flex items-center gap-1.5"><Door size={16} weight="fill" /> Do-not-disturb option</span>
            </div>
          </div>
        </div>
      </section>

      {/* LOCAL AREA STRIP */}
      <section className="bg-[#4F6F52] text-white" data-testid="local-area-strip">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 sm:py-7 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <span className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide uppercase shrink-0">
            <MapPin size={16} weight="fill" /> Proudly local
          </span>
          <p className="text-white/90 text-sm sm:text-base">
            Based in <span className="font-semibold">Moreton-in-Marsh</span> · Covering a <span className="font-semibold">20-mile radius</span> across the Cotswolds — including {VILLAGES.slice(0, 6).join(", ")} and beyond.
          </p>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24" data-testid="services-section">
        <div className="max-w-2xl">
          <span className="text-xs font-semibold tracking-[0.18em] text-[#86A789] uppercase">What we do</span>
          <h2 className="mt-2 font-[Outfit] text-3xl sm:text-4xl font-semibold text-[#2C3E35] tracking-tight">
            Honest work around your Cotswold home and garden.
          </h2>
          <p className="mt-3 text-[#73777B] text-base">
            From a one-off tidy-up to fortnightly upkeep — pick what you need, leave the rest to us.
          </p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s) => (
            <div key={s.title} className="rounded-2xl border border-[#E5E0D8] bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_12px_40px_rgb(0,0,0,0.06)] transition-shadow">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#E3EBDA] text-[#3C5A3F]">
                <s.icon size={22} weight="duotone" />
              </div>
              <h3 className="mt-4 font-[Outfit] text-xl font-semibold text-[#2C3E35]">{s.title}</h3>
              <p className="mt-2 text-sm text-[#73777B] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* VISIT PREFERENCE HIGHLIGHT */}
      <section className="bg-[#F1ECDC]/60 border-y border-[#E5E0D8]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20 grid lg:grid-cols-2 gap-10 items-center" data-testid="visit-preference-section">
          <div>
            <span className="text-xs font-semibold tracking-[0.18em] text-[#86A789] uppercase">Your home, your rules</span>
            <h2 className="mt-2 font-[Outfit] text-3xl sm:text-4xl font-semibold text-[#2C3E35] tracking-tight">
              Meet &amp; greet, or do-not-disturb — your choice.
            </h2>
            <p className="mt-3 text-[#73777B] text-base">
              Some folks like a friendly chat and a walk-around before any work begins. Others would rather we just crack on and invoice them later. Pick whichever suits you in the quote form.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-[#E5E0D8] bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#E3EBDA] text-[#3C5A3F]">
                <HandWaving size={22} weight="duotone" />
              </div>
              <h3 className="mt-4 font-[Outfit] text-lg font-semibold text-[#2C3E35]">Meet &amp; greet</h3>
              <p className="mt-2 text-sm text-[#73777B] leading-relaxed">A quick chat and a walk-around together before we start. Great for new clients or larger jobs.</p>
            </div>
            <div className="rounded-2xl border border-[#E5E0D8] bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#F1ECDC] text-[#7A5A12]">
                <Door size={22} weight="duotone" />
              </div>
              <h3 className="mt-4 font-[Outfit] text-lg font-semibold text-[#2C3E35]">Do not disturb</h3>
              <p className="mt-2 text-sm text-[#73777B] leading-relaxed">We arrive, get the job done and head off — no knock at the door. You'll get a tidy invoice afterwards.</p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white border-y border-[#E5E0D8]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24" data-testid="how-it-works-section">
          <span className="text-xs font-semibold tracking-[0.18em] text-[#86A789] uppercase">How it works</span>
          <h2 className="mt-2 font-[Outfit] text-3xl sm:text-4xl font-semibold text-[#2C3E35] tracking-tight max-w-xl">
            Three simple steps. No phone tag.
          </h2>
          <div className="mt-10 grid sm:grid-cols-3 gap-8">
            {STEPS.map((step) => (
              <div key={step.n} className="relative">
                <span className="font-[Outfit] text-5xl font-semibold text-[#D7C0AE]">{step.n}</span>
                <h3 className="mt-3 font-[Outfit] text-xl font-semibold text-[#2C3E35]">{step.title}</h3>
                <p className="mt-2 text-sm text-[#73777B] leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <Link to="/quote" data-testid="cta-get-quote-button">
              <Button className="rounded-full bg-[#4F6F52] hover:bg-[#3C5A3F] text-white px-7 h-12 text-base font-semibold">
                Get a no-obligation quote <ArrowRight weight="bold" className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20" data-testid="service-area-section">
        <span className="text-xs font-semibold tracking-[0.18em] text-[#86A789] uppercase">Service area</span>
        <h2 className="mt-2 font-[Outfit] text-3xl sm:text-4xl font-semibold text-[#2C3E35] tracking-tight max-w-xl">
          Working from Moreton-in-Marsh, out to a 20-mile radius.
        </h2>
        <p className="mt-3 text-[#73777B] max-w-2xl">
          If you're roughly within 20 miles of Moreton-in-Marsh, you're well inside our patch. Not sure? Pop your postcode into the quote form and we'll let you know.
        </p>
        <div className="mt-7 flex flex-wrap gap-2">
          {VILLAGES.map((v) => (
            <span key={v} className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E0D8] bg-white px-3 py-1.5 text-sm text-[#2C3E35]" data-testid={`village-chip-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <MapPin size={12} weight="fill" className="text-[#86A789]" /> {v}
            </span>
          ))}
          <span className="inline-flex items-center rounded-full bg-[#E3EBDA] border border-[#C9D6B7] px-3 py-1.5 text-sm text-[#3C5A3F] font-medium">…and the villages in between</span>
        </div>
      </section>

      <footer className="bg-[#1F2A23] text-white/80 mt-0">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center sm:items-start gap-6 sm:gap-10 text-center sm:text-left">
          <img src={LOGO_URL} alt="Honeystone Home & Garden" className="h-20 w-20 rounded-xl ring-1 ring-white/10" data-testid="footer-logo" />
          <div className="flex-1">
            <p className="font-[Outfit] text-lg font-semibold text-white">Honeystone Home &amp; Garden</p>
            <p className="mt-1 text-sm">Based in Moreton-in-Marsh · Serving the Cotswolds within a 20-mile radius.</p>
            <p className="mt-2 text-sm">
              Get in touch:{" "}
              <a href="mailto:Admin@honeystone.uk" className="text-white font-semibold underline decoration-[#86A789] underline-offset-2 hover:text-[#C9D6B7]" data-testid="footer-email-link">
                Admin@honeystone.uk
              </a>
            </p>
            <p className="mt-4 text-xs text-white/55">© {new Date().getFullYear()} Honeystone Home &amp; Garden. Local trades, locally trusted.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
